// frontend/src/modules/projects/api.ts
import { api } from '../../core/api-client';
import type {
  Project, CreateProjectInput, UpdateProjectInput, ProjectStatus,
  Task, CreateTaskInput, UpdateTaskInput, CreateMyTaskInput, UpdateMyTaskInput,
  TimeEntry, StartTimerInput, ManualTimeEntryInput,
  ReportRowByProject, ReportRowByEmployee, ReportRowByTask, DailyTimelineRow,
  BudgetVsActual, ProjectMilestone, CreateMilestoneInput, UpdateMilestoneInput,
  BillableTimeEntry, InvoiceTrackedTimeResult,
  ProjectMember, ProjectMemberRole,
  TimesheetApprovalStatus, TimesheetDecisionResult, UnbillTimeResult,
} from './types';

export const projectsApi = {
  // Projects
  listProjects: (status?: ProjectStatus) =>
    api<{ projects: Project[] }>(`/api/projects/projects${status ? `?status=${status}` : ''}`).then((r) => r.projects),
  getProject: (id: string) =>
    api<{ project: Project }>(`/api/projects/projects/${id}`).then((r) => r.project),
  createProject: (input: CreateProjectInput) =>
    api<{ project: Project }>('/api/projects/projects', { method: 'POST', body: input }).then((r) => r.project),
  updateProject: (id: string, patch: UpdateProjectInput) =>
    api<{ project: Project }>(`/api/projects/projects/${id}`, { method: 'PATCH', body: patch }).then((r) => r.project),
  archiveProject: (id: string) =>
    api<{ project: Project }>(`/api/projects/projects/${id}/archive`, { method: 'POST', body: {} }).then((r) => r.project),
  unarchiveProject: (id: string) =>
    api<{ project: Project }>(`/api/projects/projects/${id}/unarchive`, { method: 'POST', body: {} }).then((r) => r.project),

  // Tasks
  listTasks: (opts: { projectId?: string; assigneeEmployeeId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.projectId) qs.set('project_id', opts.projectId);
    if (opts.assigneeEmployeeId) qs.set('assignee_employee_id', opts.assigneeEmployeeId);
    return api<{ tasks: Task[] }>(`/api/projects/tasks${qs.toString() ? '?' + qs : ''}`).then((r) => r.tasks);
  },
  createTask: (input: CreateTaskInput) =>
    api<{ task: Task }>('/api/projects/tasks', { method: 'POST', body: input }).then((r) => r.task),
  updateTask: (id: string, patch: UpdateTaskInput) =>
    api<{ task: Task }>(`/api/projects/tasks/${id}`, { method: 'PATCH', body: patch }).then((r) => r.task),
  deleteTask: (id: string) =>
    api<{ ok: true }>(`/api/projects/tasks/${id}`, { method: 'DELETE' }),

  // My Tasks (Piece 4) — the caller's own/assigned tasks
  myTasks: () =>
    api<{ tasks: Task[] }>('/api/projects/tasks/me').then((r) => r.tasks),
  createMyTask: (input: CreateMyTaskInput) =>
    api<{ task: Task }>('/api/projects/tasks/me', { method: 'POST', body: input }).then((r) => r.task),
  updateMyTask: (id: string, patch: UpdateMyTaskInput) =>
    api<{ task: Task }>(`/api/projects/tasks/me/${id}`, { method: 'PATCH', body: patch }).then((r) => r.task),

  // Time entries
  getOpenEntry: () =>
    api<{ entry: TimeEntry | null }>('/api/projects/time-entries/me/open').then((r) => r.entry),
  startTimer: (input: StartTimerInput) =>
    api<{ entry: TimeEntry }>('/api/projects/time-entries/me/start', { method: 'POST', body: input }).then((r) => r.entry),
  stopTimer: (notes?: string) =>
    api<{ entry: TimeEntry }>('/api/projects/time-entries/me/stop', { method: 'POST', body: { notes } }).then((r) => r.entry),
  addManualEntry: (input: ManualTimeEntryInput) =>
    api<{ entry: TimeEntry }>('/api/projects/time-entries/me', { method: 'POST', body: input }).then((r) => r.entry),
  myTimeEntries: (opts: { projectId?: string; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.projectId) qs.set('project_id', opts.projectId);
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    return api<{ rows: TimeEntry[] }>(`/api/projects/time-entries/me${qs.toString() ? '?' + qs : ''}`).then((r) => r.rows);
  },
  listTimeEntries: (opts: { employeeId?: string; projectId?: string; taskId?: string; from?: string; to?: string; approvalStatus?: TimesheetApprovalStatus } = {}) => {
    const qs = new URLSearchParams();
    if (opts.employeeId) qs.set('employee_id', opts.employeeId);
    if (opts.projectId) qs.set('project_id', opts.projectId);
    if (opts.taskId) qs.set('task_id', opts.taskId);
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    if (opts.approvalStatus) qs.set('approval_status', opts.approvalStatus);
    return api<{ rows: TimeEntry[] }>(`/api/projects/time-entries${qs.toString() ? '?' + qs : ''}`).then((r) => r.rows);
  },

  // ── Timesheet approval (migration 077) ──
  // The opt-in flag lives on tenant_settings.require_timesheet_approval; this
  // read is open to anyone who can see their own time so every screen can show
  // the right status chip. Flipping it is PATCH /api/tenant/settings
  // (settings.write), not a projects route.
  timesheetApprovalRequired: () =>
    api<{ require_timesheet_approval: boolean }>('/api/projects/time-entries/approval-required')
      .then((r) => r.require_timesheet_approval),

  /**
   * Approve / reject / send back one or many entries. All three need
   * `time_entries.approve`; the server refuses a decision on an already
   * invoiced entry (409 `entry_invoiced`) and on the caller's OWN hours
   * unless they are an owner (403 `own_time_entry_approval`).
   * `note` is only stored on a rejection.
   */
  decideTimeEntries: (
    decision: TimesheetApprovalStatus,
    entryIds: string[],
    note?: string | null,
  ) => {
    const path = decision === 'approved' ? 'approve' : decision === 'rejected' ? 'reject' : 'reopen';
    return api<TimesheetDecisionResult>(`/api/projects/time-entries/${path}`, {
      method: 'POST',
      body: { entry_ids: entryIds, note: note ?? null },
    });
  },
  updateTimeEntry: (id: string, patch: Partial<ManualTimeEntryInput>) =>
    api<{ entry: TimeEntry }>(`/api/projects/time-entries/${id}`, { method: 'PATCH', body: patch }).then((r) => r.entry),
  deleteTimeEntry: (id: string) =>
    api<{ ok: true }>(`/api/projects/time-entries/${id}`, { method: 'DELETE' }),

  // Budgets
  getBudget: (projectId: string) =>
    api<{ budget: BudgetVsActual }>(`/api/projects/projects/${projectId}/budget`).then((r) => r.budget),

  // Milestones
  listMilestones: (projectId: string) =>
    api<{ milestones: ProjectMilestone[] }>(`/api/projects/projects/${projectId}/milestones`).then((r) => r.milestones),
  createMilestone: (projectId: string, input: CreateMilestoneInput) =>
    api<{ milestone: ProjectMilestone }>(`/api/projects/projects/${projectId}/milestones`, { method: 'POST', body: input }).then((r) => r.milestone),
  updateMilestone: (mid: string, patch: UpdateMilestoneInput) =>
    api<{ milestone: ProjectMilestone }>(`/api/projects/projects/milestones/${mid}`, { method: 'PATCH', body: patch }).then((r) => r.milestone),
  deleteMilestone: (mid: string) =>
    api<{ ok: true }>(`/api/projects/projects/milestones/${mid}`, { method: 'DELETE' }),

  // Members (migration 080) — the project roster
  listMembers: (projectId: string) =>
    api<{ members: ProjectMember[] }>(`/api/projects/projects/${projectId}/members`).then((r) => r.members),
  addMember: (projectId: string, input: { employee_id: string; role?: ProjectMemberRole }) =>
    api<{ member: ProjectMember }>(`/api/projects/projects/${projectId}/members`, { method: 'POST', body: input }).then((r) => r.member),
  removeMember: (projectId: string, employeeId: string) =>
    api<{ ok: true }>(`/api/projects/projects/${projectId}/members/${employeeId}`, { method: 'DELETE' }),

  // Billable time + invoice-from-time
  billableTime: (opts: { projectId?: string; clientId?: string; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.projectId) qs.set('project_id', opts.projectId);
    if (opts.clientId) qs.set('client_id', opts.clientId);
    if (opts.from) qs.set('from', opts.from);
    if (opts.to) qs.set('to', opts.to);
    return api<{ entries: BillableTimeEntry[] }>(`/api/projects/billable-time${qs.toString() ? '?' + qs : ''}`).then((r) => r.entries);
  },
  /**
   * Release tracked time billed to a CANCELLED invoice so those hours become
   * billable again. Same permission as issuing the invoice (`invoices.write`)
   * — it is the inverse of that action. 409 `invoice_not_cancelled` while the
   * invoice is still live; omit `entryIds` to release everything on it.
   */
  unbillTime: (invoiceId: string, entryIds?: string[]) =>
    api<UnbillTimeResult>('/api/projects/unbill-time', {
      method: 'POST',
      body: { invoice_id: invoiceId, entry_ids: entryIds },
    }),

  invoiceTime: (input: {
    project_id?: string; client_id?: string; from?: string; to?: string;
    group_by?: 'task' | 'project'; entry_ids?: string[]; due_at?: string;
  }) =>
    api<InvoiceTrackedTimeResult>('/api/projects/invoice-time', { method: 'POST', body: input }),

  // Reports
  reportByProject: (from: string, to: string, employeeId?: string) =>
    api<{ rows: ReportRowByProject[] }>(`/api/projects/reports/by-project?from=${from}&to=${to}${employeeId ? `&employee_id=${employeeId}` : ''}`).then((r) => r.rows),
  reportByEmployee: (from: string, to: string, projectId?: string) =>
    api<{ rows: ReportRowByEmployee[] }>(`/api/projects/reports/by-employee?from=${from}&to=${to}${projectId ? `&project_id=${projectId}` : ''}`).then((r) => r.rows),
  reportByTask: (from: string, to: string, projectId?: string, employeeId?: string) => {
    const qs = new URLSearchParams({ from, to });
    if (projectId) qs.set('project_id', projectId);
    if (employeeId) qs.set('employee_id', employeeId);
    return api<{ rows: ReportRowByTask[] }>(`/api/projects/reports/by-task?${qs}`).then((r) => r.rows);
  },
  timeline: (from: string, to: string, employeeId?: string, projectId?: string) => {
    const qs = new URLSearchParams({ from, to });
    if (employeeId) qs.set('employee_id', employeeId);
    if (projectId) qs.set('project_id', projectId);
    return api<{ rows: DailyTimelineRow[] }>(`/api/projects/reports/timeline?${qs}`).then((r) => r.rows);
  },
};
